import React, { useEffect, useState } from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { fetchData, setPaymentProcess } from 'actions';
import { WithPaymentCompletedRedirection } from 'components/HOC/WithPaymentCompletedRedirection/WithPaymentCompletedRedirection';
import { InvalidRecipient } from 'components/InvalidRecipient/InvalidRecipient';
import { Notifications } from 'components/Notifications/Notifications';
import { PaymentSetup } from 'components/PaymentSetup/PaymentSetup';
import { RedirectToSelectInstitution } from 'components/RedirectToSelectInstitution/RedirectToSelectInstitution';
import { Spinner } from 'components/Spinner/Spinner';
import { Autologout } from 'containers/Autologout/Autologout';
import type { RootState } from 'reducers/types';
import {
  getCurrentLocale,
  getFieldValue,
  getRecipientId,
  isRecipientActive,
} from 'selectors';

type PaymentProcessProps = ConnectedProps<typeof connector>;

const PaymentProcessComponent = ({
  setPaymentProcess,
  fetchData,
  recipient,
  hasRecipient,
  isRecipientActive,
  locale,
  ...props
}: PaymentProcessProps) => {
  const [isFetching, setIsFetching] = useState(true);
  const [searchParams] = useSearchParams();

  function stopFetching() {
    setIsFetching(false);
  }

  function getRecipientCode() {
    const destination = searchParams.get('destination');
    const code = searchParams.get('recipient');

    return destination || code || recipient;
  }

  const recipientCode = getRecipientCode();

  useEffect(() => {
    setPaymentProcess();
  }, [setPaymentProcess]);

  useEffect(() => {
    fetchData(recipientCode as string).then(stopFetching);
  }, [fetchData, locale, recipientCode]);

  if (!recipientCode) {
    return <RedirectToSelectInstitution />;
  }
  if (isFetching) {
    return <Spinner />;
  }
  if (!hasRecipient || !isRecipientActive) {
    return (
      <>
        <Notifications />
        <InvalidRecipient />
      </>
    );
  }

  return (
    <Autologout>
      <PaymentSetup {...props} />
    </Autologout>
  );
};

const mapStateToProps = (state: RootState) => ({
  recipient: getFieldValue(state, 'recipient'),
  hasRecipient: !!getRecipientId(state),
  isRecipientActive: isRecipientActive(state),
  locale: getCurrentLocale(state),
});

const mapDispatchToProps = {
  fetchData,
  setPaymentProcess,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const PaymentProcess = connector(
  WithPaymentCompletedRedirection(PaymentProcessComponent),
);

export { PaymentProcess };
